import React, { useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { Modal, ButtonGroup, Panel, PanelGroup, Button, designSystem } from '@yola/ws-ui';
import bowser from 'yola-bowser';
import { context as viewportContext } from 'yola-editor/src/js/utils/viewport-provider';
import CustomCodeSection from 'src/js/modules/common/components/custom-code-section';
import customCodeTriggerIds from 'src/js/modules/common/constants/custom-code-trigger-ids';
import constants from '../constants';
import SeoSection from './seo-section';
import OpenGraphSection from './open-graph-section';
import GeneralSection from './general-section';
import dialogTypes from '../../dialogs/constants/dialog-types';

const { Tabs, TabsList, Tab, TabsContentList, TabContent, Heading } = designSystem;
const { sizes, tabIdentifiers } = constants;

const PageSettings = ({
  activeTabId,
  control,
  triggerValidation,
  fieldRefs,
  captions,
  pageId,
  pathPrefix,
  businessName,
  languageCodeOptions,
  isHomePage,
  isDisplayInNavigationAvailable,
  isPasswordProtected,
  isPasswordProtectedPagesFeatureAvailable,
  isPasswordProtectedPagesFeatureFlagEnabled,
  isBusinessInfoInWebsiteSettingsFeatureFlagEnabled,
  showPromptForce,
  formValues,
  isValid,
  onOpenGraphImageChange,
  onEnablePasswordProtection,
  onDisablePasswordProtection,
  onTabChange,
  onSubmit,
  onCancel,
  onKeyDown,
  onConfirmExitSubmit,
  onConfirmExitCancel,
  showPageSettingsDialog,
}) => {
  const { width, height } = useContext(viewportContext);
  const modalWidth =
    width >= sizes.LARGE_RES_WIDTH ? sizes.MODAL_LARGE_WIDTH : sizes.MODAL_MEDIUM_WIDTH;
  const modalHeight =
    height >= sizes.LARGE_RES_HEIGHT ? sizes.MODAL_LARGE_HEIGHT : sizes.MODAL_MEDIUM_HEIGHT;

  const scrollContainerRef = useRef();

  return (
    <div className="ws-page-settings">
      <Modal
        width={modalWidth}
        height={modalHeight}
        className="ws-page-settings__modal"
        dragHandleSelector=".ws-page-settings__header"
        promptCaptions={captions.confirmExit}
        invalidPromptCaptions={captions.discardChanges}
        isValid={isValid}
        overlay="visible"
        centered
        draggable={false}
        resizable={false}
        fullscreen={bowser.mobile}
        showPromptForce={showPromptForce}
        data={formValues}
        onKeyDown={onKeyDown}
        handleSubmit={onConfirmExitSubmit}
        handleCancel={onConfirmExitCancel}
      >
        <PanelGroup height="100%">
          <Panel
            theme="gray-100"
            corners="squared"
            className="ws-panel--shrink ws-page-settings__header"
          >
            <Heading type="heading-6">{captions.pageSettings}</Heading>
          </Panel>
          <div className="ws-page-settings__tabs">
            <Tabs
              activeTabId={activeTabId}
              onTabChange={onTabChange}
              scrollContainerRef={scrollContainerRef}
            >
              <TabsList>
                <Tab tabId={tabIdentifiers.GENERAL} label={captions.general.tabLabel} />
                <Tab tabId={tabIdentifiers.SEO} label={captions.seoSettings.tabLabel} />
                <Tab tabId={tabIdentifiers.OPEN_GRAPH} label={captions.openGraph.tabLabel} />
                <Tab tabId={tabIdentifiers.CUSTOM_CODE} label={captions.customCode.tabLabel} />
              </TabsList>
              <TabsContentList>
                <TabContent tabId={tabIdentifiers.GENERAL}>
                  <GeneralSection
                    control={control}
                    fieldRefs={fieldRefs}
                    captions={captions.general}
                    pageId={pageId}
                    pathPrefix={pathPrefix}
                    languageCodeOptions={languageCodeOptions}
                    isPathDisabled={isHomePage}
                    isDisplayInNavigationAvailable={isDisplayInNavigationAvailable}
                    isPasswordProtected={isPasswordProtected}
                    isPasswordProtectedPagesFeatureAvailable={
                      isPasswordProtectedPagesFeatureAvailable
                    }
                    isPasswordProtectedPagesFeatureFlagEnabled={
                      isPasswordProtectedPagesFeatureFlagEnabled
                    }
                    onEnablePasswordProtection={onEnablePasswordProtection}
                    onDisablePasswordProtection={onDisablePasswordProtection}
                  />
                </TabContent>
                <TabContent tabId={tabIdentifiers.SEO}>
                  <SeoSection
                    control={control}
                    triggerValidation={triggerValidation}
                    fieldRefs={fieldRefs}
                    captions={captions.seoSettings}
                    pathPrefix={pathPrefix}
                    businessName={businessName}
                    isBusinessInfoInWebsiteSettingsFeatureFlagEnabled={
                      isBusinessInfoInWebsiteSettingsFeatureFlagEnabled
                    }
                  />
                </TabContent>
                <TabContent tabId={tabIdentifiers.OPEN_GRAPH}>
                  <OpenGraphSection
                    control={control}
                    fieldRefs={fieldRefs}
                    captions={captions.openGraph}
                    pathPrefix={pathPrefix}
                    businessName={businessName}
                    isBusinessInfoInWebsiteSettingsFeatureFlagEnabled={
                      isBusinessInfoInWebsiteSettingsFeatureFlagEnabled
                    }
                    showPageSettingsDialog={showPageSettingsDialog}
                    onOpenGraphImageChange={onOpenGraphImageChange}
                  />
                </TabContent>
                <TabContent tabId={tabIdentifiers.CUSTOM_CODE}>
                  <CustomCodeSection
                    triggerId={customCodeTriggerIds.PAGE_SETTINGS}
                    dialogType={dialogTypes.PAGE_SETTINGS}
                    control={control}
                    captions={captions.customCode}
                    showSettingsDialog={(formData) =>
                      showPageSettingsDialog({ activeTabId: tabIdentifiers.CUSTOM_CODE, formData })
                    }
                  />
                </TabContent>
              </TabsContentList>
            </Tabs>
          </div>
          <Panel corners="squared" className="ws-page-settings__footer">
            <ButtonGroup>
              <Button
                className="ws-page-settings__button ws-page-settings__button--apply"
                size="large"
                type="default"
                stretch="block"
                onClick={onSubmit}
              >
                {captions.apply}
              </Button>
              <Button
                className="ws-page-settings__button"
                size="large"
                type="default"
                stretch="block"
                onClick={onCancel}
              >
                {captions.cancel}
              </Button>
            </ButtonGroup>
          </Panel>
        </PanelGroup>
      </Modal>
    </div>
  );
};

PageSettings.propTypes = {
  activeTabId: PropTypes.string.isRequired,
  control: PropTypes.shape().isRequired,
  triggerValidation: PropTypes.func.isRequired,
  fieldRefs: PropTypes.shape().isRequired,
  captions: PropTypes.shape().isRequired,
  pageId: PropTypes.string.isRequired,
  pathPrefix: PropTypes.string.isRequired,
  businessName: PropTypes.string.isRequired,
  languageCodeOptions: PropTypes.array.isRequired,
  isHomePage: PropTypes.bool.isRequired,
  isDisplayInNavigationAvailable: PropTypes.bool.isRequired,
  isPasswordProtected: PropTypes.bool.isRequired,
  isPasswordProtectedPagesFeatureAvailable: PropTypes.bool.isRequired,
  isPasswordProtectedPagesFeatureFlagEnabled: PropTypes.bool.isRequired,
  isBusinessInfoInWebsiteSettingsFeatureFlagEnabled: PropTypes.bool.isRequired,
  showPromptForce: PropTypes.bool.isRequired,
  formValues: PropTypes.shape({}).isRequired,
  isValid: PropTypes.bool.isRequired,
  onOpenGraphImageChange: PropTypes.func.isRequired,
  onEnablePasswordProtection: PropTypes.func.isRequired,
  onDisablePasswordProtection: PropTypes.func.isRequired,
  onTabChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func.isRequired,
  onConfirmExitSubmit: PropTypes.func.isRequired,
  onConfirmExitCancel: PropTypes.func.isRequired,
  showPageSettingsDialog: PropTypes.func.isRequired,
};

export default PageSettings;
